import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import ProfileDropdown from "./ProfileDropdown";
import NotificationDropdown from "./Notification";
import { getUserDetails } from "apis/homeServices";
import { getUserProfile, getProfilePicture } from "apis/settingsServices";
import { useUserStore } from "store/userStore";
import { extractAirlineCode } from "utils/stringUtils";
import NotificationBell from "assets/images/notification.png";
import AvatarPlaceholder from "assets/images/avatar-placeholder.svg";
import StandbyLogo from "assets/images/standby.png";
import ReferralModal from "./ReferralCard";
import HeaderFavouritesDropdown from "./HeaderFavouritesDropdown";
import {
  isAdminRole,
  isLifetimeOgUser,
  isOgUser,
  isUserSideRole,
} from "utils/roles";
import { LifetimeOgBadge, OgUserBadge } from "./OgBadges";

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isFavouritesOpen, setIsFavouritesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useUserStore((state) => state.role);
  const primaryAirlineCode = useUserStore((state) => state.primaryAirlineCode);
  const profilePictureUrl = useUserStore((state) => state.profilePictureUrl);

  // "Invite a Friend" and "Contact Us" are user-side only (regular users and
  // core-committee / community users). Admins see neither — just Log Out.
  const profileItems = isUserSideRole(userRole)
    ? [
        {
          label: "Invite a Friend",
          icon: "st-invite",
          onClick: () => setIsReferralModalOpen(true),
        },
        {
          label: "Contact Us",
          icon: "st-chat",
          onClick: () =>
            navigate("/settings?section=contactUs", {
              state: { section: "contactUs" },
            }),
        },
        {
          label: "Settings",
          icon: "st-settings",
          onClick: () => navigate("/settings"),
        },
      ]
    : [];

  const [userDetails, setUserDetails] = useState<any>(() => {
    try {
      const savedUserDetails = localStorage.getItem("userDetails");
      return savedUserDetails ? JSON.parse(savedUserDetails) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response: any = await getUserDetails();
        if (response) {
          setUserDetails(response);
          if (response.userName) {
            useUserStore.getState().setUsername(response.userName);
          }
          if (response.email) {
            useUserStore.getState().setEmail(response.email);
          }
          useUserStore.getState().setUserRole(response.role ?? null);
          if (response.referralCode) {
            useUserStore.getState().setReferralCode(response.referralCode);
          }
          if (response.primaryAirlineCode) {
            useUserStore
              .getState()
              .setPrimaryAirlineCode(response.primaryAirlineCode);
          }
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    };

    fetchUserDetails();
  }, []);

  // Admins read notifications from "admin_notifications" (giveaway-ended job
  // etc.); regular/community users read from "load_notifications".
  const notificationCollectionName = isAdminRole(userRole)
    ? "admin_notifications"
    : "load_notifications";

  // Fetch user profile to get primary airline code
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profileData = await getUserProfile();
        if (profileData?.primaryAirline?.airlineName) {
          const airlineCode = extractAirlineCode(profileData.primaryAirline.airlineName);
          if (airlineCode) {
            useUserStore.getState().setPrimaryAirlineCode(airlineCode);
          }
        }
      } catch (error) {
        console.error("Failed to fetch user profile for airline code:", error);
      }
    };

    // Only fetch if we don't already have the airline code
    if (!primaryAirlineCode && isUserSideRole(userRole)) {
      fetchUserProfile();
    }
  }, [primaryAirlineCode, userRole]);

  // Fetch the profile picture into the store so the header avatar reflects the
  // user's uploaded photo (Settings updates the same store on upload). Only
  // fetch when it's not already cached from a previous session / upload.
  //
  // Every route builds its own MainLayout, so this component remounts on each
  // navigation — the cache check is what keeps that from firing a request per
  // page view. Logout clears the cache (see resetUserDetails), so the next user
  // re-fetches instead of inheriting this one's photo.
  useEffect(() => {
    // Guards against a race with Settings' own upload flow: this effect can
    // still have a fetch in flight (started while profilePictureUrl was
    // still null) at the moment the user uploads a new photo elsewhere on
    // the same page load. profilePictureUrl changing re-runs this effect,
    // but the OLD in-flight request's response was still landing — without
    // this flag, applying it here would overwrite the just-uploaded photo
    // with the stale (pre-upload) one a moment later, which is exactly what
    // "the photo reverts after a few seconds" looked like.
    let ignore = false;

    const fetchProfilePicture = async () => {
      try {
        const data: any = await getProfilePicture();
        if (!ignore && data?.profilePictureUrl) {
          // Cache-busted so this exact request can't be served from the
          // browser's cache of an earlier (pre-upload) fetch of the same
          // URL — see Profile.tsx's withCacheBust for the full story.
          const url = data.profilePictureUrl;
          const bustedUrl = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
          useUserStore.getState().setProfilePictureUrl(bustedUrl);
        }
      } catch (error) {
        console.warn("No profile picture available", error);
      }
    };

    if (!profilePictureUrl) {
      fetchProfilePicture();
    }

    return () => {
      ignore = true;
    };
  }, [profilePictureUrl]);

  return (
    <header className="relative z-30 bg-white border-b border-[#E5E7EB] px-3 sm:px-4 md:px-6 py-3">
      <div className="flex items-center justify-between md:justify-end gap-2 sm:gap-3">
        {/* Hamburger menu — mobile only */}
        <button
          type="button"
          onClick={onToggleSidebar}
          className="md:hidden p-1.5 rounded-lg text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors shrink-0"
          aria-label="Open navigation menu"
        >
          <svg
            className="w-6 h-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Logo — shown on mobile only (the sidebar carries it on desktop). */}
        <button
          type="button"
          onClick={() =>
            navigate(isAdminRole(userRole) ? "/admin/dashboard" : "/search")
          }
          className="md:hidden flex-shrink-0 flex items-center"
          aria-label="Standby home"
        >
          <img
            src={StandbyLogo}
            alt="Standby"
            className="h-8 w-auto object-contain"
          />
        </button>

        {/* Search icon */}
        {/* <button
          type="button"
          className="p-2 text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button> */}

        {/* Favourites */}
        {isUserSideRole(userRole) && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsFavouritesOpen((prev) => !prev)}
              className="w-12 h-12 max-h-12 rounded-[18px] border border-gray-200/80 bg-white hover:bg-gray-50 flex items-center justify-center transition-colors shrink-0 shadow-xs"
              title="Favourites"
            >
              <Heart width={22} height={22} className="text-gray-900" strokeWidth={2} />
            </button>
            <HeaderFavouritesDropdown
              isOpen={isFavouritesOpen}
              onClose={() => setIsFavouritesOpen(false)}
            />
          </div>
        )}

        {/* Notification */}
        {(isUserSideRole(userRole) || isAdminRole(userRole)) && (
          <NotificationDropdown
            icon={NotificationBell}
            collectionName={notificationCollectionName}
          />
        )}

          {/* Profile — each OG tier carries its own badge next to the name. */}
          <ProfileDropdown
            userAvatar={profilePictureUrl || AvatarPlaceholder}
            items={profileItems}
            badge={
              isLifetimeOgUser(userRole) ? (
                <LifetimeOgBadge />
              ) : isOgUser(userRole) ? (
                <OgUserBadge />
              ) : undefined
            }
          />
        </div>

      <ReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
      />
    </header>
  );
};

export default Header;
